import {
  Alert,
  Button,
  DatePicker,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { mapStarError } from 'pages/star-shared/star-error';
import { KcRow, StarConfig } from 'pages/star-shared/types';
import { formatDate, formatNumber } from 'pages/star-shared/utils';
import { usePagedList } from 'pages/star-shared/use-paged-list';
import StarAmount from 'pages/star-shared/components/star-amount';
import UserPicker, { UserPickerValue } from 'pages/star-shared/components/user-picker';
import KcEvaluateModal from './components/kc-evaluate-modal';
import { colors } from 'theme/tokens';

const columns: ColumnsType<KcRow> = [
  {
    title: 'Creator',
    key: 'user',
    render: (_, r) => (
      <div>
        <div style={{ fontWeight: 600 }}>{r.nickName || `#${r.userId}`}</div>
        <div style={{ color: colors.textSecondary, fontSize: 12 }}>#{r.userId}</div>
      </div>
    ),
  },
  {
    title: 'TikTok UID',
    dataIndex: 'tiktokUid',
    key: 'tiktokUid',
    width: 160,
    render: (v) => v || '—',
  },
  { title: 'Ngày', key: 'kcDate', width: 110, render: (_, r) => formatDate(r.kcDate) },
  { title: 'KC', key: 'kc', width: 120, align: 'right', render: (_, r) => formatNumber(r.kc) },
  {
    title: 'Mốc đạt',
    key: 'tierStar',
    width: 110,
    align: 'right',
    render: (_, r) => (r.tierStar ? <StarAmount value={r.tierStar} strong={false} /> : '—'),
  },
  {
    title: 'Sao đã cấp',
    key: 'starGiven',
    width: 110,
    align: 'right',
    render: (_, r) => <StarAmount value={r.starGiven} />,
  },
  {
    title: 'Nguồn',
    dataIndex: 'sourceFile',
    key: 'sourceFile',
    // sourceFile: "manual:star" khi sửa tay (form ở trang Nhập dữ liệu), còn lại là tên file TikTok đã nhập.
    render: (v) => {
      if (!v) return '—';
      if (String(v).startsWith('manual')) return <Tag color="orange">Nhập tay</Tag>;
      return (
        <Tooltip title={v}>
          <Tag style={{ maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</Tag>
        </Tooltip>
      );
    },
  },
];

const StarKcPage = () => {
  const { loaded, isAdmin, canImportData } = useCurrentRoles();
  const [month, setMonth] = useState<Dayjs>(dayjs());
  const [user, setUser] = useState<UserPickerValue>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [evaluateOpen, setEvaluateOpen] = useState(false);
  const [config, setConfig] = useState<StarConfig | null>(null);

  const params = useMemo(
    () => ({ month: month.format('YYYY-MM'), userId: user?.value }),
    [month, user]
  );
  const { items, loading } = usePagedList<KcRow>(API.starsKc, params, { refreshKey });
  const triggerRefresh = () => setRefreshKey((k) => k + 1);

  // Mốc KC nằm ở cấu hình (chỉ ADMIN đọc được) — MOD không gọi để tránh 403 → đăng xuất.
  useEffect(() => {
    if (!loaded || !isAdmin) return undefined;
    let cancelled = false;
    const fetchConfig = async () => {
      try {
        const res: any = await AxiosUtils.get(API.starsConfig);
        if (!cancelled) setConfig(res?.data || null);
      } catch (err) {
        if (!cancelled)
          notification.warning({ message: `Không tải được mốc KC: ${mapStarError(err)}` });
      }
    };
    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, [loaded, isAdmin]);

  const totalKc = items.reduce((sum, r) => sum + (Number(r.kc) || 0), 0);
  const totalStar = items.reduce((sum, r) => sum + (Number(r.starGiven) || 0), 0);
  const tiers = [...(config?.kcTiers || [])].sort((a, b) => a.kc - b.kc);

  return (
    <div>
      <PageTitle title="Data Kim cương" />
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <>
            File TikTok và sửa tay từng dòng nằm ở{' '}
            {canImportData ? (
              <Link to="/data-import">Doanh thu → Nhập dữ liệu</Link>
            ) : (
              'Doanh thu → Nhập dữ liệu'
            )}
            . Trang này để xem KC từng ngày và kết quả chấm Sao.
          </>
        }
      />
      {tiers.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Typography.Text strong>Mốc KC ngày: </Typography.Text>
          <Space size={4} wrap>
            {tiers.map((t) => (
              <Tag key={t.kc} color="geekblue">
                ≥ {formatNumber(t.kc)} KC → {formatNumber(t.star)} ⭐
              </Tag>
            ))}
          </Space>
          <Typography.Text type="secondary">
            {' '}
            (lấy mốc cao nhất đạt được, không cộng dồn)
          </Typography.Text>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <DatePicker
          picker="month"
          allowClear={false}
          value={month}
          format="MM/YYYY"
          onChange={(value) => value && setMonth(value)}
        />
        <div style={{ width: 300 }}>
          <UserPicker value={user} onChange={setUser} placeholder="Lọc theo Creator" />
        </div>
        <Button icon={<ReloadOutlined />} onClick={triggerRefresh} loading={loading}>
          Tải lại
        </Button>
        <div style={{ flex: 1 }} />
        <Button icon={<SyncOutlined />} onClick={() => setEvaluateOpen(true)}>
          Chấm lại khoảng ngày
        </Button>
      </div>
      <div className="custom-table">
        <Table
          columns={columns}
          rowKey={(r) => `${r.userId}-${r.kcDate}`}
          dataSource={items}
          loading={loading}
          locale={{ emptyText: 'Chưa có dữ liệu KC trong tháng này.' }}
          pagination={{ pageSize: 31, showSizeChanger: false, showLessItems: true }}
          scroll={{ x: 900 }}
          summary={() =>
            items.length ? (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={3}>
                    <strong>Tổng ({items.length} dòng)</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <strong>{formatNumber(totalKc)}</strong>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} />
                  <Table.Summary.Cell index={5} align="right">
                    <StarAmount value={totalStar} />
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={6} />
                </Table.Summary.Row>
              </Table.Summary>
            ) : null
          }
        />
      </div>
      <KcEvaluateModal
        open={evaluateOpen}
        onClose={() => setEvaluateOpen(false)}
        onSuccess={triggerRefresh}
      />
    </div>
  );
};

export default StarKcPage;
